import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";

/**
 * Compact video upload trigger — click to open a file picker, uploads
 * to /api/admin/uploads/video (15 MB cap enforced backend-side), then
 * calls onUploaded(url). Twin of ImageUploader but for MP4/WebM/MOV.
 */
export default function VideoUploader({
    onUploaded,
    testid = "video-uploader",
    label = "Upload video",
    size = "default",
}) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const pick = () => inputRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-picking the same file
        if (!file) return;
        if (!file.type.startsWith("video/")) {
            toast.error("Please choose a video file (MP4 / WebM / MOV)");
            return;
        }
        // Match backend cap; nicer failure than a 400.
        if (file.size > 15 * 1024 * 1024) {
            toast.error("Video too large — please keep under 15 MB");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/uploads/video", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Video uploaded");
            onUploaded?.(data.url);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={handleFile}
                data-testid={`${testid}-input`}
                className="sr-only"
            />
            <Button
                type="button"
                onClick={pick}
                disabled={busy}
                data-testid={`${testid}-btn`}
                variant="outline"
                size={size === "sm" ? "sm" : "default"}
                className={
                    size === "sm"
                        ? "rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-8 text-xs"
                        : "w-full rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-10"
                }
            >
                {busy ? (
                    <>
                        <Loader2 size={12} className="mr-1.5 animate-spin" />
                        Uploading…
                    </>
                ) : (
                    <>
                        <Upload size={12} className="mr-1.5" />
                        {label}
                    </>
                )}
            </Button>
        </>
    );
}
